import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 全局 Alt 键状态管理器（单例模式）
 * 用于统一管理所有卡片组件的 Alt 键状态，避免多个组件实例状态不同步
 */
class AltKeyManager {
  private altKeyPressed = false;
  private listeners = new Set<(pressed: boolean) => void>();
  private mousePosition = { x: 0, y: 0 };
  private currentPreviewPinId: string | null = null; // 当前显示放大框的卡片ID
  private pinIdListeners = new Set<(pinId: string | null) => void>();

  constructor() {
    if (typeof window === "undefined") return;

    // 全局鼠标位置跟踪
    window.addEventListener("mousemove", (e) => {
      this.mousePosition = { x: e.clientX, y: e.clientY };
    });

    // 全局 Alt 键监听
    window.addEventListener("keydown", (e) => {
      if (e.key === "Alt" || e.keyCode === 18) {
        if (e.repeat) return; // 防止重复触发
        if (!this.altKeyPressed) {
          this.altKeyPressed = true;
          this.notifyListeners(true);
        }
      }
    });

    window.addEventListener("keyup", (e) => {
      if (e.key === "Alt" || e.keyCode === 18) {
        if (this.altKeyPressed) {
          this.altKeyPressed = false;
          this.currentPreviewPinId = null; // 清除当前预览的卡片ID
          this.notifyListeners(false);
          this.notifyPinIdListeners(null);
        }
      }
    });

    // 窗口失去焦点时清除状态
    window.addEventListener("blur", () => {
      if (this.altKeyPressed) {
        this.altKeyPressed = false;
        this.currentPreviewPinId = null; // 清除当前预览的卡片ID
        this.notifyListeners(false);
        this.notifyPinIdListeners(null);
      }
    });
  }

  private notifyListeners(pressed: boolean) {
    this.listeners.forEach((listener) => {
      try {
        listener(pressed);
      } catch (error) {
        console.error("[AltKeyManager] Listener error:", error);
      }
    });
  }

  private notifyPinIdListeners(pinId: string | null) {
    this.pinIdListeners.forEach((listener) => {
      try {
        listener(pinId);
      } catch (error) {
        console.error("[AltKeyManager] PinId listener error:", error);
      }
    });
  }

  subscribe(listener: (pressed: boolean) => void) {
    this.listeners.add(listener);
    // 立即通知当前状态
    listener(this.altKeyPressed);
    return () => {
      this.listeners.delete(listener);
    };
  }

  subscribePinId(listener: (pinId: string | null) => void) {
    this.pinIdListeners.add(listener);
    // 立即通知当前状态
    listener(this.currentPreviewPinId);
    return () => {
      this.pinIdListeners.delete(listener);
    };
  }

  isPressed(): boolean {
    return this.altKeyPressed;
  }

  getMousePosition(): { x: number; y: number } {
    return { ...this.mousePosition };
  }

  getCurrentPreviewPinId(): string | null {
    return this.currentPreviewPinId;
  }

  setCurrentPreviewPinId(pinId: string | null) {
    if (this.currentPreviewPinId !== pinId) {
      this.currentPreviewPinId = pinId;
      this.notifyPinIdListeners(pinId);
    }
  }
}

// 单例实例
const altKeyManager =
  typeof window !== "undefined" ? new AltKeyManager() : null;

/**
 * Hook: 获取全局 Alt 键状态
 * @returns {boolean} Alt 键是否按下
 */
export function useAltKeyState(): boolean {
  const [altKeyPressed, setAltKeyPressed] = useState(false);

  useEffect(() => {
    if (!altKeyManager) return;

    const unsubscribe = altKeyManager.subscribe((pressed) => {
      setAltKeyPressed(pressed);
    });

    return unsubscribe;
  }, []);

  return altKeyPressed;
}

/**
 * Hook: 获取全局鼠标位置
 * @returns {{ x: number; y: number }} 鼠标位置
 */
export function useGlobalMousePosition(): { x: number; y: number } {
  const [position, setPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    if (!altKeyManager) return;

    const updatePosition = () => {
      setPosition(altKeyManager.getMousePosition());
    };

    // 初始位置
    updatePosition();

    // 定期更新位置（避免频繁更新）
    const interval = setInterval(updatePosition, 100);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return position;
}

/**
 * Hook：获取当前预览的卡片ID
 */
export function useCurrentPreviewPinId(): string | null {
  const [currentPinId, setCurrentPinId] = useState<string | null>(null);

  useEffect(() => {
    if (!altKeyManager) return;

    const unsubscribe = altKeyManager.subscribePinId((pinId) => {
      setCurrentPinId(pinId);
    });

    return unsubscribe;
  }, []);

  return currentPinId;
}

/**
 * Hook：设置当前预览的卡片ID
 */
export function useSetCurrentPreviewPinId() {
  return useCallback((pinId: string | null) => {
    if (altKeyManager) {
      altKeyManager.setCurrentPreviewPinId(pinId);
    }
  }, []);
}

/**
 * 获取 Alt 键管理器实例（用于非 Hook 场景）
 */
export function getAltKeyManager(): AltKeyManager | null {
  return altKeyManager;
}
